import { useStructureTableSort } from "../../hooks/useStructureTableSort";
import { ORIENTATIONS, ProbePadSortKey } from "../../DesignUploadStep.values";
import { SortIcon } from "@/components/projects/steps/DesignUploadStep/components/icons/SortIcon";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Structure } from "@/types/designs";
import { DesignTableCell } from "../DesignTableCell";

type ProbePadDraft = NonNullable<Structure["probePads"]>[number];

type ProbePadsTableProps = {
  isEditing?: boolean;
  draftPads: ProbePadDraft[];
  onDraftPadsChange: (draftPads: ProbePadDraft[]) => void;
};

export function ProbePadsTable({
  isEditing = false,
  draftPads,
  onDraftPadsChange,
}: ProbePadsTableProps) {
  const { sortedRows, handleSort, sortState } = useStructureTableSort<ProbePadSortKey>(draftPads, {
    key: "name",
    direction: "asc",
  });

  const updatePad = <K extends keyof ProbePadDraft>(
    id: number,
    key: K,
    value: ProbePadDraft[K],
  ) => {
    onDraftPadsChange(draftPads.map((pad) => (pad.id === id ? { ...pad, [key]: value } : pad)));
  };

  return (
    <div className="m-0">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[220px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("name")}
              >
                Name <SortIcon sortState={sortState} key="name" />
              </button>
            </TableHead>
            <TableHead className="w-[220px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("orientation")}
              >
                Orientation <SortIcon sortState={sortState} key="orientation" />
              </button>
            </TableHead>
            <TableHead className="w-[120px] text-right">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("x")}
              >
                X <SortIcon sortState={sortState} key="x" />
              </button>
            </TableHead>
            <TableHead className="w-[120px] text-right">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("y")}
              >
                Y <SortIcon sortState={sortState} key="y" />
              </button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.length > 0 ? (
            sortedRows.map((pad) => (
              <TableRow key={pad.id}>
                <TableCell className="w-[220px]">
                  <DesignTableCell
                    value={pad.name}
                    isEditing={isEditing}
                    onChange={(value) => updatePad(pad.id, "name", String(value))}
                  />
                </TableCell>
                <TableCell className="w-[220px]">
                  <DesignTableCell
                    value={pad.orientation}
                    isEditing={isEditing}
                    options={ORIENTATIONS}
                    inputType="dropdown"
                    onChange={(value) => updatePad(pad.id, "orientation", String(value))}
                  />
                </TableCell>
                <TableCell className="w-[120px] text-right">
                  <DesignTableCell
                    value={pad.x}
                    isEditing={isEditing}
                    inputType="number"
                    inputClassName="h-8 w-full text-right"
                    onChange={(value) => updatePad(pad.id, "x", Number(value))}
                  />
                </TableCell>
                <TableCell className="w-[120px] text-right">
                  <DesignTableCell
                    value={pad.y}
                    isEditing={isEditing}
                    inputType="number"
                    inputClassName="h-8 w-full text-right"
                    onChange={(value) => updatePad(pad.id, "y", Number(value))}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                No probe pads available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
